import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { loadHelperSession } from "@/pages/FestivalHelperRedeem";

// Whitelist consulted ONLY for the hard-gated player states
// (`pending_details` = onboarding incomplete, `rejected` = admin denied).
// Those two states cannot proceed and are pinned to a minimal set of pages.
// `pending` (under coach review) is soft-gated: it falls through the guard
// entirely and can browse offerings/find-coach/progress with booking locked
// at the button level inside Offerings.jsx.
const PENDING_PLAYER_ALLOWED = new Set([
  "/dashboard",
  "/notifications",
  "/help",
  "/profile/personal",
  "/account/password",
  "/onboarding/media-consent",
  "/onboarding/verify-email",
]);

function isPendingPlayer(user) {
  // Only the two states that TRULY can't proceed are hard-gated. `pending`
  // (under coach review) is soft — see StudentDashboard for the banner UX.
  return user?.role === "student"
    && (user.player_status === "pending_details"
        || user.player_status === "rejected");
}

/**
 * Media-consent gate — every logged-in user must answer their subject consent
 * question (photos/videos of themselves). Coaches must ALSO acknowledge their
 * data-controller responsibilities. Bumping MEDIA_CONSENT_VERSION on the
 * backend re-prompts every user on their next mount.
 *
 * Iter68 fix: skip this gate entirely when the user hasn't verified their
 * email yet. Without the skip, the two gates ping-ponged for any fresh
 * signup with BOTH flags active (email_verified=false AND
 * media_consent_needed=true) — the email-verify gate would send them to
 * /onboarding/verify-email, then the media-consent gate would send them
 * back to /onboarding/media-consent, and React eventually killed the tab
 * with 'Maximum update depth exceeded'. Verify email FIRST, consent SECOND.
 */
function needsMediaConsent(user) {
  if (!user) return false;
  if (user.must_change_password) return false;
  if (user.email_verified === false) return false;
  if (user.media_consent_needed) return true;
  if ((user.role === "coach" || user.role === "admin") && user.coach_media_ack_needed) return true;
  return false;
}

export default function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Iter247 — Festival Helper Pass bypass. A helper who has redeemed
  // their pass (short-code entered on /festival/:fid/helper/:passId)
  // holds a scoped JWT in localStorage under `rl_festival_helper_token`
  // — they do NOT have a Rally-Lab account. Skip the auth gate entirely
  // when we're rendering a route under that helper's festival; backend
  // route handlers already gate role/scope via X-Festival-Helper-Token.
  // Session expiry is honoured inside loadHelperSession() (returns null
  // once the pass expiry has passed), so an expired shift automatically
  // falls through to the standard Rally-Lab auth flow.
  const helper = loadHelperSession();
  if (helper && helper.festival_id
      && location.pathname.startsWith(`/festival/${helper.festival_id}/`)) {
    return children;
  }
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]">
      <div className="label-mono">Loading…</div>
    </div>
  );
  // Iter94e — `null` means "we don't know yet" (transient network failure on
  // /auth/me — retrying in background). Only `false` is a confirmed logout,
  // where the backend explicitly said 401/403. This prevents Safari from
  // bouncing users to /login just because a request timed out during a
  // navigation.
  if (user === null) return (
    <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]">
      <div className="label-mono">Reconnecting…</div>
    </div>
  );
  if (user === false) return <Navigate to="/login" replace />;
  if (!user) return <Navigate to="/login" replace />;

  // Blocking onboarding steps, in priority order: password change, then email
  // verification, then media consent. Only the first pending step applies, and
  // once the user is on that step's page no later gate may redirect them away
  // (prevents ping-pong loops with the coach / pending-player / self-rating gates).
  const step = user.must_change_password ? "/account/password"
    : user.email_verified === false ? "/onboarding/verify-email"
    : needsMediaConsent(user) ? "/onboarding/media-consent"
    : null;
  if (step) {
    return location.pathname === step ? children : <Navigate to={step} replace />;
  }

  // Gate unapproved coaches
  if (user.role === "coach" && user.coach_status !== "approved") {
    if (user.coach_status === "incomplete" || !user.coach_status) {
      return <Navigate to="/coach/apply" replace />;
    }
    return <Navigate to="/coach/pending" replace />;
  }

  // Gate unapproved players — they can view their profile + dashboard banner,
  // but nothing else (including self-rating) until admin approves them.
  // Iter329 · Bug 9 — Festival organisers are NEVER pinned to this
  // pending-player whitelist. Their `player_status` remains
  // `pending_details` because they never self-rate (they don't
  // play), but they're an organiser, not a player. Without this
  // skip, the guard bounces them off /festival/mine → /dashboard,
  // then the organiser rule on /dashboard bounces them straight
  // back to /festival/mine, producing an infinite redirect loop
  // (surfaced as a black screen post-OTP).
  if (isPendingPlayer(user) && !user.is_festival_organiser && !PENDING_PLAYER_ALLOWED.has(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Iter310 — Festival Organiser routing. Organisers use their user
  // account under a student `role` + the additive `is_festival_organiser`
  // flag (iter303). Skip the mandatory self-rating gate (they don't play
  // in a festival, they run one) AND bounce them off /dashboard onto the
  // festival area on first sign-in. Coaches with the flag keep their
  // existing dashboard.
  if (user.is_festival_organiser
      && user.role !== "coach"
      && user.role !== "admin"
      && location.pathname === "/dashboard") {
    return <Navigate to="/festival/mine" replace />;
  }

  // Mandatory pickleball self-rating — only for APPROVED players.
  // (Before approval we don't want the player pinning their own DUPR level.)
  // Iter310 — Festival organisers are NEVER pushed through the self-rating
  // gate. They aren't players; the coaching onboarding doesn't apply.
  if (user.role === "student"
      && !isPendingPlayer(user)
      && !user.is_festival_organiser
      && !user.self_rating
      && location.pathname !== "/onboarding/self-rating") {
    return <Navigate to="/onboarding/self-rating" replace />;
  }

  if (role === "coach" && !(user.role === "coach" || user.role === "admin")) {
    return <Navigate to="/dashboard" replace />;
  }
  if (role === "admin" && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}
